import * as echarts from 'echarts/lib/echarts';

const MAX_PERCENTAGE = 100;
const LOW_ALARM_COLOR = [37, 99, 235];
const MEDIUM_ALARM_COLOR = [245, 158, 11];
const HIGH_ALARM_COLOR = [220, 38, 38];
const MEDIUM_ALARM_PERCENTAGE = 30;
const HIGH_ALARM_PERCENTAGE = 60;
const BAR_AXIS_PADDING_PERCENTAGE = 20;
const BAR_AXIS_LIMIT = MAX_PERCENTAGE + BAR_AXIS_PADDING_PERCENTAGE;

const getAlarmColor = (percentage) => {
  const normalizedPercentage = Math.max(0, Math.min(MAX_PERCENTAGE, percentage));

  if (normalizedPercentage < MEDIUM_ALARM_PERCENTAGE) {
    return LOW_ALARM_COLOR;
  }

  if (normalizedPercentage < HIGH_ALARM_PERCENTAGE) return MEDIUM_ALARM_COLOR;

  return HIGH_ALARM_COLOR;
};

const interpolateColor = (start, end, ratio) => start.map((component, index) => Math.round(
  component + ((end[index] - component) * ratio),
));

const rgbColor = color => `rgb(${color.join(',')})`;

const getSeriesColor = (side, percentage) => {
  const color = getAlarmColor(percentage);
  const highlight = interpolateColor(color, [255, 255, 255], 0.2);
  const shadow = interpolateColor(color, [0, 0, 0], 0.15);

  return new echarts.graphic.LinearGradient(
    side === 'closing' ? 1 : 0,
    0,
    side === 'closing' ? 0 : 1,
    0,
    [
      { offset: 0, color: rgbColor(highlight) },
      { offset: 1, color: rgbColor(shadow) },
    ],
  );
};

const formatPercentage = (percentage, locale = 'pt-BR') => (
  Number(Number(percentage).toFixed(2)).toLocaleString(locale)
);

const getValueLabel = (value, persistenceMode, locale) => (
  persistenceMode === 'quantity'
    ? `${value.count}/${value.total}`
    : `${formatPercentage(value.percentage, locale)}%`
);

const createSeries = ({
  rows,
  side,
  name,
  persistenceMode,
  fontColor,
  gridColor,
  isClosing = false,
  locale,
}) => ({
  name,
  type: 'bar',
  barWidth: 22,
  barGap: '20%',
  barCategoryGap: '45%',
  data: rows.map((row) => {
    const value = row[side];

    return {
      value: isClosing ? -value.percentage : value.percentage,
      label: {
        show: true,
        position: isClosing ? 'left' : 'right',
        color: fontColor,
        fontWeight: 600,
        formatter: getValueLabel(value, persistenceMode, locale),
      },
      itemStyle: {
        color: getSeriesColor(side, value.percentage),
        borderRadius: isClosing ? [5, 0, 0, 5] : [0, 5, 5, 0],
      },
    };
  }),
  markLine: {
    symbol: 'none',
    silent: true,
    label: { show: false },
    lineStyle: { color: gridColor, width: 1, type: 'dashed' },
    data: [{ xAxis: 0 }],
  },
});

const createGraphicLabels = ({
  isMotorMode,
  closingLabel,
  openingLabel,
  motorLabel,
  secondaryFontColor,
}) => {
  const labels = [
    {
      type: 'text',
      left: isMotorMode ? '75%' : '44%',
      top: 72,
      style: {
        text: (isMotorMode ? motorLabel : closingLabel).toUpperCase(),
        textAlign: 'center',
        font: '700 14px sans-serif',
        fill: secondaryFontColor,
      },
      silent: true,
    },
  ];

  if (!isMotorMode) {
    labels.push({
      type: 'text',
      left: '76%',
      top: 72,
      style: {
        text: openingLabel.toUpperCase(),
        textAlign: 'center',
        font: '700 14px sans-serif',
        fill: secondaryFontColor,
      },
      silent: true,
    });
  }

  return labels;
};

export const generate = (rows, options = {}) => {
  const {
    alarmMode = 'breaker',
    persistenceMode = 'percentage',
    labels = {},
    tooltipFormatter,
    fontColor = '#fff',
    secondaryFontColor = '#fff',
    gridColor = 'rgba(255, 255, 255, .12)',
    tooltipBackgroundColor = '#3d4143',
    tooltipBorderColor = '#8d8d8d',
    locale = 'pt-BR',
  } = options;
  const chartRows = Array.isArray(rows) ? rows : [];
  const chartLabels = {
    title: '',
    closing: '',
    opening: '',
    motor: '',
    ...labels,
  };
  const isMotorMode = alarmMode === 'motor';
  const series = isMotorMode
    ? [createSeries({
      rows: chartRows,
      side: 'motor',
      name: chartLabels.motor,
      persistenceMode,
      fontColor,
      gridColor,
    })]
    : [
      createSeries({
        rows: chartRows,
        side: 'closing',
        name: chartLabels.closing,
        persistenceMode,
        fontColor,
        gridColor,
        isClosing: true,
        locale,
      }),
      createSeries({
        rows: chartRows,
        side: 'opening',
        name: chartLabels.opening,
        persistenceMode,
        fontColor,
        gridColor,
        locale,
      }),
    ];

  return {
    backgroundColor: 'transparent',
    animationDuration: 400,
    title: {
      text: chartLabels.title,
      left: 32,
      top: 14,
      textStyle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: fontColor,
      },
    },
    grid: {
      left: '28%',
      right: '6%',
      top: 104,
      bottom: 42,
      // Keep the variable names in the column below the chart title.
      containLabel: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: tooltipBackgroundColor,
      borderColor: tooltipBorderColor,
      textStyle: { color: fontColor },
      formatter: tooltipFormatter,
    },
    xAxis: {
      type: 'value',
      min: isMotorMode ? 0 : -BAR_AXIS_LIMIT,
      max: BAR_AXIS_LIMIT,
      interval: isMotorMode ? 20 : 50,
      axisLabel: {
        color: secondaryFontColor,
        formatter: value => `${Math.abs(value)}%`,
      },
      splitLine: {
        lineStyle: { color: gridColor },
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: chartRows.map(row => row.label || row.name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        interval: 0,
        color: fontColor,
        fontSize: 14,
        width: 280,
        overflow: 'truncate',
        margin: 18,
      },
    },
    graphic: createGraphicLabels({
      isMotorMode,
      closingLabel: chartLabels.closing,
      openingLabel: chartLabels.opening,
      motorLabel: chartLabels.motor,
      secondaryFontColor,
    }),
    series,
  };
};

export { getAlarmColor, formatPercentage, interpolateColor };

export default {
  generate,
  getAlarmColor,
  formatPercentage,
  interpolateColor,
};
